import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Printer, Mail } from "lucide-react";

const SECTIONS = [
  {
    n: 1,
    title: "Application and acceptance",
    body: [
      "The Organizer may review applications for product fit, quality, category balance, and available space. Acceptance applies only to the business, products, and category described in the approved application. An application does not guarantee a space.",
      "The Exhibitor must disclose any material change in products or ownership and obtain approval before offering items outside the accepted scope. Booth assignments are made by the Organizer and may be adjusted for operational or safety reasons.",
    ],
  },
  {
    n: 2,
    title: "Products",
    body: [
      "Products must meet the handmade, original, or curated criteria presented to event organizers during the jury process. The Exhibitor must accurately describe products and may not substitute mass-produced or resale goods for approved items without written approval. All merchandise must be suitable for a family-friendly holiday event. The Organizer may require removal of unapproved, unsafe, unlawful, or misrepresented products.",
      {
        heading: "Booth Presentation",
        text: "This Event is expected to serve the Brambleton community with an exceptional, peaceful, exclusive Holiday-themed event. Exhibitors are expected to present their products and themselves in a professional manner. Organizer reserves the right to require any Exhibitor to remove offensive decorations or display items, clothing, or other items that could distract shoppers from anything other than a family-friendly Holiday-themed event.",
      },
    ],
  },
  {
    n: 3,
    title: "Fees and payment",
    body: [
      "A space is reserved only when the required payment is received. Any balance must be paid by the date on the invoice. The Organizer may release an unpaid space after notifying the Exhibitor.",
      "Each Exhibitor is responsible for their own tables, equipment, supplies, payment processing, table cover, and other operating expenses unless expressly included in writing.",
    ],
  },
  {
    n: 4,
    title: "Cancellation and event changes",
    body: [
      "Exhibitor cancellation requests must be sent in writing to the Organizer no less than 14 days prior to the event. Refund eligibility and any transfer or credit are governed by the cancellation schedule as follows:",
      {
        list: [
          "Notification within 14 days prior to the event entitles the Exhibitor to a 100% refund of registration fees.",
          "Notification between 13 days and 7 days of the event entitles the Exhibitor to a 50% refund of registration fees.",
          "Notification any less than 7 days prior to the event entitles the Exhibitor to a 0% refund of registration fees.",
        ],
      },
      "If the Event must be canceled, postponed, shortened, or materially changed because of weather, a venue issue, government action, safety concern, or another circumstance beyond the Organizer's reasonable control, the Organizer will communicate the available options and any applicable refund.",
    ],
  },
  {
    n: 5,
    title: "Schedule and attendance",
    body: [
      "The Event is scheduled for Friday, November 20 and Saturday, November 21, 2026, from 10:00 a.m. to 6:00 p.m. each day. Exhibitors must be fully set up before opening, staff their booths during all public hours, and remain open until closing on both days. Setup, check-in, vehicle access, and breakdown instructions will be provided before the Event, and many options will be provided to the Exhibitor to set up well in advance of the event.",
      {
        heading: "Breakdown",
        text: "Breakdown will begin no earlier than Saturday after the closing of the event at 6 p.m. All vendors are required to be completely moved out of their space by Saturday 10 p.m. on the closing day of the event. Early or late breakdown or an unattended booth requires the Organizer's approval 3 days in advance of the event.",
      },
    ],
  },
  {
    n: 6,
    title: "Space and venue rules + electric",
    body: [
      "This event must accommodate large groups of shoppers and lines MUST be avoided. Exhibitors must do their best to assist in the flow of shopper traffic and remain within their assigned footprint, keeping aisles, exits, fire equipment, and accessible routes clear. Exhibitors are encouraged to decorate their space using fixtures, furniture, carpets/rugs, etc. Exhibitors are also encouraged to set their space up to 'make' while shoppers shop.",
      "However, the following items will be prohibited due to insurance restrictions:",
      {
        list: [
          "NO open flames, heaters, candles, or heat tools.",
          "NO tents, canopies, or other types of roof cover.",
          "NO attaching items to the walls or floors.",
          "NO signs or banners larger than 3' × 8'.",
          "ANY fixture MUST comply with all venue and fire safety rules. If there is any question as to compliance, please consult with the Organizer in advance.",
        ],
      },
      "Access to electric will be available for all exhibitors at no extra fee. However, use of extension cords, lighting, and other items requiring power will be closely monitored for compliance with safety regulations. If you need to use power, please plan to bring your own extension cord and limit your access to ONE outlet if possible. The Organizer will do their best to make sure everyone has equal access to power.",
    ],
  },
  {
    n: 7,
    title: "Sales & sales reporting",
    body: [
      "Exhibitors make their own sales, collect payment, issue receipts, and handle returns and customer service. They are responsible for applicable business registrations, Virginia sales tax, licenses, product labeling, and compliance with laws that apply to their products.",
      "Food or beverage sellers must obtain any required health permits and provide proof on request. All food and beverage items to be sold at the event must be approved in advance by the Organizer.",
    ],
  },
  {
    n: 8,
    title: "Conduct and safety",
    body: [
      "Exhibitors and their representatives must treat patrons, other vendors, venue staff, and Event staff respectfully and follow reasonable onsite instructions. Smoking or vaping is permitted only where the venue allows it. Pets are not permitted in vendor spaces except service animals as required by law. Children accompanying an Exhibitor must be supervised.",
      "The Organizer may require a hazardous activity to stop or remove an Exhibitor whose conduct or display presents a safety risk or materially violates these terms. Removal for an Exhibitor's breach does not entitle the Exhibitor to a refund.",
    ],
  },
  {
    n: 9,
    title: "Property and insurance",
    body: [
      "Each Exhibitor is encouraged to carry appropriate general liability and property coverage. If any Exhibitor cannot obtain their own insurance, an alternative will be made available. All insurance coverage requests will occur the week prior to the event when the Organizer sends out requests for COI.",
    ],
  },
  {
    n: 10,
    title: "Photography and promotion",
    body: [
      "The Exhibitor grants the Organizer permission to use the business name, product images, and photographs or video of the booth and products taken at the Event to promote this Event and future Organizer events, without additional payment. The Exhibitor confirms it has permission to provide any submitted images and may notify the Organizer in writing of a specific image it does not own or cannot license. This permission does not transfer ownership of the Exhibitor's work.",
    ],
  },
  {
    n: 11,
    title: "Responsibility and disputes",
    body: [
      "Each party remains responsible for its own acts and omissions. To the extent permitted by Virginia law, the Exhibitor will reimburse the Organizer for third-party claims, losses, and reasonable expenses arising from the Exhibitor's products, operations, or breach of these terms, except to the extent caused by the Organizer's negligence or misconduct.",
      "These terms are governed by Virginia law. The parties will first try to resolve any dispute directly in good faith; any legal action shall be brought in a court with jurisdiction in Loudoun County, Virginia.",
    ],
  },
  {
    n: 12,
    title: "Agreement and communications",
    body: [
      "These terms, the accepted application, the invoice, and written Event instructions form the participation agreement. If they conflict, a specific written term in the accepted application or invoice controls over this document for booth price, inclusions, and payment dates. Changes to the agreement must be in writing from the Organizer.",
      "Notices should be sent to the contact details provided in the application and to the Organizer at info@artisanmarketandstudio.com.",
    ],
  },
];

function renderBlock(block, idx) {
  if (typeof block === "string") {
    return (
      <p key={idx} className="font-body text-lg leading-relaxed text-[color:var(--ed-forest-dark)]/90">
        {block}
      </p>
    );
  }
  if (block.list) {
    return (
      <ul key={idx} className="space-y-2 pl-5 list-disc font-body text-lg leading-relaxed text-[color:var(--ed-forest-dark)]/90 marker:text-[color:var(--ed-terracotta)]">
        {block.list.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }
  if (block.heading) {
    return (
      <div key={idx}>
        <p className="kicker text-[color:var(--ed-terracotta)] mb-2">{block.heading}</p>
        <p className="font-body text-lg leading-relaxed text-[color:var(--ed-forest-dark)]/90">{block.text}</p>
      </div>
    );
  }
  return null;
}

export default function FestivalOfTreesTermsPage() {
  return (
    <div className="editorial min-h-screen bg-[color:var(--ed-cream)]" data-testid="festival-terms-page">
      <Helmet>
        <title>Exhibitor Terms & Conditions – The Holiday Festival of Trees 2026</title>
        <meta
          name="description"
          content="Exhibitor Terms & Conditions for The Holiday Festival of Trees at the Barn at Brambleton, November 20–21, 2026."
        />
      </Helmet>

      {/* Print-friendly styling: hide nav/footer + non-print controls when printing */}
      <style>{`
        @media print {
          nav, footer, [data-print-hide="true"] { display: none !important; }
          body, .editorial { background: #fff !important; }
          .terms-section { break-inside: avoid; }
        }
      `}</style>

      {/* Header band */}
      <section className="relative bg-[color:var(--ed-forest-dark)] text-[#FDF7EA] px-6 py-16 md:py-20">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/events/festival-of-trees-2026"
            className="inline-flex items-center gap-2 text-sm opacity-90 hover:opacity-100 mb-8"
            data-testid="terms-back-link"
            data-print-hide="true"
          >
            <ArrowLeft className="w-4 h-4" /> Back to the event
          </Link>
          <div>
            <span className="kicker text-[color:var(--ed-gold)]">Exhibitor Terms &amp; Conditions</span>
          </div>
          <h1 className="mt-3 text-4xl md:text-6xl font-editorial leading-[1.05]" data-testid="terms-title">
            The Holiday Festival of Trees at the Barn at Brambleton
          </h1>
          <p className="mt-4 font-body text-lg md:text-xl text-[#FDF7EA]/85">
            November 20 &amp; 21, 2026 · 24439 Evergreen Mills Road, Ashburn, Virginia
          </p>

          <div className="mt-8 flex flex-wrap gap-3" data-print-hide="true">
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--ed-gold)] text-[color:var(--ed-forest-dark)] font-body font-semibold hover:brightness-105 transition"
              data-testid="terms-print-btn"
            >
              <Printer className="w-4 h-4" /> Print / Save as PDF
            </button>
            <a
              href="mailto:info@artisanmarketandstudio.com?subject=Festival%20of%20Trees%202026%20%E2%80%94%20Exhibitor%20Terms%20Question"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#FDF7EA]/40 text-[#FDF7EA] font-body font-semibold hover:bg-[#FDF7EA]/10 transition"
              data-testid="terms-email-btn"
            >
              <Mail className="w-4 h-4" /> Ask a question
            </a>
          </div>
        </div>
      </section>

      {/* Preamble + sections */}
      <section className="max-w-4xl mx-auto px-6 py-16 md:py-20">
        <p className="font-body text-lg leading-relaxed text-[color:var(--ed-forest-dark)]/90" data-testid="terms-preamble">
          These terms govern participation by accepted artists, makers, and other approved vendors
          (collectively, &ldquo;Exhibitors&rdquo;) in The Holiday Festival of Trees at the Barn at
          Brambleton (the &ldquo;Event&rdquo;), organized by Artisan Market &amp; Studio (the
          &ldquo;Organizer&rdquo;). The Event takes place at The Barn at Brambleton, 24439
          Evergreen Mills Road, Ashburn, Virginia. By accepting a space and submitting payment, the
          Exhibitor agrees to these terms and the Event details supplied with the accepted
          application.
        </p>

        <div className="mt-14 space-y-14" data-testid="terms-sections">
          {SECTIONS.map((s) => (
            <article
              key={s.n}
              className="terms-section"
              data-testid={`terms-section-${s.n}`}
              id={`section-${s.n}`}
            >
              <div className="flex items-baseline gap-4 mb-5">
                <span className="font-editorial text-4xl md:text-5xl text-[color:var(--ed-terracotta)] leading-none">
                  {String(s.n).padStart(2, "0")}
                </span>
                <h2 className="font-editorial text-3xl md:text-4xl leading-tight text-[color:var(--ed-forest-dark)]">
                  {s.title}
                </h2>
              </div>
              <div className="space-y-4 pl-0 md:pl-16">
                {s.body.map((b, i) => renderBlock(b, i))}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-16 p-6 md:p-8 rounded-2xl border-2 border-[color:var(--ed-terracotta)]/30 bg-[color:var(--ed-terracotta)]/5" data-testid="terms-reminder">
          <p className="kicker text-[color:var(--ed-terracotta)]">Reminder</p>
          <p className="mt-2 font-editorial text-2xl md:text-3xl leading-snug text-[color:var(--ed-forest-dark)]">
            Payment of the invoice for the event is agreement to these terms and conditions.
          </p>
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 text-sm text-[color:var(--ed-muted)] font-body" data-print-hide="true">
          <p>Questions? Email <a href="mailto:info@artisanmarketandstudio.com" className="underline hover:text-[color:var(--ed-terracotta)]">info@artisanmarketandstudio.com</a>.</p>
          <Link
            to="/events/festival-of-trees-2026"
            className="inline-flex items-center gap-2 hover:text-[color:var(--ed-terracotta)]"
            data-testid="terms-bottom-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to the event
          </Link>
        </div>
      </section>
    </div>
  );
}
